import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, 
  TrendingDown, 
  ChevronDown, 
  ChevronUp, 
  FileText, 
  Newspaper,
  Activity,
  BarChart3,
  MessageSquare,
  Clock,
  AlertCircle,
  Info,
  Zap,
  Users,
  Calendar,
  ExternalLink,
  Target,
  DollarSign,
  Percent,
  Download,
  Bell
} from 'lucide-react';
import Sparkline from '../DashboardComponents/SparklineDashboard';
import StockLogo from '../../../components/StockLogo/StockLogoRender';
import StockChart from './StockChart';
import DashboardPostComposer from '../DashboardComponents/StockerPostComposer';
import { InfoTooltip } from './Tooltip';
import { getFilingTooltip } from './TooltipFilings';
/**
 * ✅ PRODUCTION-READY EXPANDABLE STOCK CARD
 * 
 * Features:
 * - Context Radar integration (events, filings, news)
 * - Stocker Signal (rings: price, social, news, filings)
 * - Sentiment Aggregator (credible, retail, noise, groups)
 * - Technical analysis with indicators
 * - Real-time sparkline
 * - Compliance-safe language throughout
 * - Full tab implementations (Overview, Summary, Sentiment, Chart, Events, Post)
 * - Keyboard shortcuts
 * - Export functionality
 * - Mobile responsive
 * - Complete Redux data mapping with multiple fallbacks
 */


/**
 * WHY IT MATTERS (COMPLIANCE-SAFE - OBSERVATIONAL ONLY)
 */
const getWhyItMatters = (evt, t) => {
  const type = evt.event_type;
  const p = 'dashboard.expandableStockCard.whyItMatters';

  if (type === 'filing') {
    return t(`${p}.filing`);
  }

  if (type === 'news') {
    const headline = (evt.headline || '').toLowerCase();
    if (headline.includes('earnings') || headline.includes('revenue')) return t(`${p}.earnings`);
    if (headline.includes('acquisition') || headline.includes('merger')) return t(`${p}.acquisition`);
    if (headline.includes('partnership') || headline.includes('deal')) return t(`${p}.partnership`);
    if (headline.includes('lawsuit') || headline.includes('investigation')) return t(`${p}.lawsuit`);
    if (headline.includes('product') || headline.includes('launch')) return t(`${p}.product`);
    if (headline.includes('analyst') || headline.includes('upgrade')) return t(`${p}.analyst`);
    if (headline.includes('ceo') || headline.includes('executive')) return t(`${p}.ceo`);
    return t(`${p}.newsDefault`);
  }

  if (type === 'sentiment') return t(`${p}.sentiment`);
  if (type === 'abnormal') return t(`${p}.abnormal`);
  return t(`${p}.default`);
};

/**
 * Helper functions
 */
const formatNumber = (value, decimals = 2, t) => {
  if (value === null || value === undefined || isNaN(Number(value))) return t('dashboard.expandableStockCard.na');
  return Number(value).toFixed(decimals);
};

const formatPercentValue = (value, decimals = 2, t) => {
  if (value === null || value === undefined || isNaN(Number(value))) return t('dashboard.expandableStockCard.na');
  const num = Number(value);
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(decimals)}%`;
};

const formatTimeAgo = (evt, t) => {
  const ts = evt?.published_at || evt?.created_at || evt?.timestamp;
  if (!ts) return t('dashboard.expandableStockCard.recently');
  const date = new Date(ts);
  if (isNaN(date)) return t('dashboard.expandableStockCard.recently');
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('dashboard.expandableStockCard.justNow');
  if (mins < 60) return t('dashboard.expandableStockCard.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('dashboard.expandableStockCard.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('dashboard.expandableStockCard.daysAgo', { count: days });
};

/**
 * MAIN COMPONENT
 */
const ExpandableStockCard = React.memo(({
  stock,
  isExpanded,
  onToggle,
  onCreatePost,
  chartData,
  indicators,
  currentTimeFrame = '1D',
  currentIndicators = [],
  onTimeFrameChange,
  onIndicatorsChange,
  contextEvents = [],
  signal = null,
  sentimentAgg = null,
  recentPosts = [],
  technicalContext = null,
  isLoading = false,
  onExport,
  onSetAlert,
}) => {
  const { t } = useTranslation();
  const [showPostComposer, setShowPostComposer] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [composerContext, setComposerContext] = useState(null);
  const [showStickyActions, setShowStickyActions] = useState(false);
  
  const cardRef = useRef(null);
  const contentRef = useRef(null);

  // Scroll into view when expanded
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [isExpanded]);

  // Sticky actions on scroll
  useEffect(() => {
    if (!isExpanded || !contentRef.current) return;
    const handleScroll = (e) => setShowStickyActions(e.target.scrollTop > 100);
    const content = contentRef.current;
    content.addEventListener('scroll', handleScroll);
    return () => content.removeEventListener('scroll', handleScroll);
  }, [isExpanded]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isExpanded) return;
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const shortcuts = {
        '1': () => setActiveTab('overview'),
        '2': () => setActiveTab('summary'),
        '3': () => setActiveTab('sentiment'),
        '4': () => setActiveTab('chart'),
        '5': () => setActiveTab('context'),
        'p': () => setActiveTab('post'),
        'Escape': () => onToggle()
      };
      shortcuts[e.key]?.();
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isExpanded, onToggle]);

  // === COMPUTED DATA ===
  
  // Recency timeline
  const recencyTimeline = useMemo(() => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const events = (contextEvents || [])
      .map(e => {
        const ts = new Date(e.published_at || e.created_at).getTime();
        return { ...e, ts, age: now - ts };
      })
      .filter(e => !isNaN(e.ts))
      .sort((a, b) => b.ts - a.ts);
    return {
      last24h: events.filter(e => e.age < day),
      last7d: events.filter(e => e.age >= day && e.age < 7 * day),
      last30d: events.filter(e => e.age >= 7 * day && e.age < 30 * day)
    };
  }, [contextEvents]);

  // Chart interpretation (COMPLIANCE-SAFE)
  const chartInterpretation = useMemo(() => {
    const ci = 'dashboard.expandableStockCard.chartInterpretation';
    if (!chartData || chartData.length === 0) {
      return { summary: t(`${ci}.insufficientData`), trend: 'neutral', details: [] };
    }
    const prices = chartData
      .map(d => d.period_close ?? d.avg_close ?? d.close ?? d.c ?? d.price ?? 0)
      .filter(p => !isNaN(p) && isFinite(p));
    if (prices.length < 2) {
      return { summary: t(`${ci}.needMoreData`), trend: 'neutral', details: [] };
    }
    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const changePercent = ((lastPrice - firstPrice) / firstPrice) * 100;
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const variance = prices.reduce((sum, p) => sum + Math.pow(p - avgPrice, 2), 0) / prices.length;
    const volatility = Math.sqrt(variance);
    const volatilityPct = (volatility / avgPrice) * 100;

    let trend = 'neutral';
    let summary = '';

    // COMPLIANCE-SAFE LANGUAGE
    if (changePercent > 5) {
      trend = 'bullish';
      summary = t(`${ci}.bullish`, { pct: changePercent.toFixed(1), timeframe: currentTimeFrame });
    } else if (changePercent > 1) {
      trend = 'slightly-bullish';
      summary = t(`${ci}.slightlyBullish`, { pct: changePercent.toFixed(1), timeframe: currentTimeFrame });
    } else if (changePercent < -5) {
      trend = 'bearish';
      summary = t(`${ci}.bearish`, { pct: changePercent.toFixed(1), timeframe: currentTimeFrame });
    } else if (changePercent < -1) {
      trend = 'slightly-bearish';
      summary = t(`${ci}.slightlyBearish`, { pct: changePercent.toFixed(1), timeframe: currentTimeFrame });
    } else {
      summary = t(`${ci}.stable`, { sign: changePercent > 0 ? '+' : '', pct: changePercent.toFixed(1), timeframe: currentTimeFrame });
    }

    const details = [];
    if (volatilityPct > 5) details.push(t(`${ci}.highVolatility`, { pct: volatilityPct.toFixed(1) }));
    else if (volatilityPct > 2) details.push(t(`${ci}.moderateVolatility`, { pct: volatilityPct.toFixed(1) }));
    else details.push(t(`${ci}.lowVolatility`, { pct: volatilityPct.toFixed(1) }));

    if (indicators?.RSI?.length) {
      const rsi = indicators.RSI.at(-1);
      if (rsi > 70) details.push(t(`${ci}.rsiOverbought`));
      else if (rsi < 30) details.push(t(`${ci}.rsiOversold`));
    }

    if (indicators?.MACD?.length && indicators?.MACD_Signal?.length) {
      const macdLine = indicators.MACD.at(-1);
      const signalLine = indicators.MACD_Signal.at(-1);
      if (macdLine > signalLine) details.push(t(`${ci}.macdBullish`));
      else details.push(t(`${ci}.macdBearish`));
    }

    return { summary, trend, details };
  }, [chartData, indicators, currentTimeFrame, t]);

  // Stock summary (from Stocker Signal)
  const stockSummary = useMemo(() => {
    const km = 'dashboard.expandableStockCard.keyMetrics';
    if (!signal?.rings) {
      return {
        summary: t('dashboard.expandableStockCard.selectStockToView'),
        keyMetrics: [],
        recentActivity: [],
        contextualInsights: [],
        overall: null
      };
    }

    const rings = signal.rings;
    const recent_events = signal.recent_events || [];
    const overall = signal.overall || {};
    const price_action = rings.price_action || {};
    const social = rings.social || {};
    const news = rings.news || {};
    const filings = rings.filings || {};

    const volumeRatio = price_action.volume_ratio_1d ?? price_action.volume_ratio;
    const keyMetrics = [];

    // Overall metrics
    if (typeof overall.score === 'number') {
      keyMetrics.push({
        label: t(`${km}.overallSignalScore`),
        value: formatNumber(overall.score, 2, t),
        context: t(`${km}.compositeScoreContext`, { window: signal.window || '1d' }),
        type: 'overall'
      });
    }
    if (overall.direction) {
      keyMetrics.push({
        label: t(`${km}.overallDirection`),
        value: overall.direction,
        context: t(`${km}.drivenByContext`, { driver: overall.primary_driver || t(`${km}.mixedFactors`) }),
        type: 'overall'
      });
    }
    if (typeof overall.confidence === 'number') {
      keyMetrics.push({
        label: t(`${km}.signalConfidence`),
        value: `${Math.round(overall.confidence <= 1 ? overall.confidence * 100 : overall.confidence)}%`,
        context: t(`${km}.strengthOfEvidence`),
        type: 'overall'
      });
    }

    // Price metrics
    if (typeof price_action.change_pct_1d === 'number') {
      keyMetrics.push({
        label: t(`${km}.oneDayPriceMovement`),
        value: formatPercentValue(price_action.change_pct_1d, 2, t),
        context: price_action.change_pct_1d > 0 ? t(`${km}.priceUpIn24h`) : price_action.change_pct_1d < 0 ? t(`${km}.priceDownIn24h`) : t(`${km}.priceFlatIn24h`),
        type: 'price'
      });
    }
    if (typeof volumeRatio === 'number') {
      keyMetrics.push({
        label: t(`${km}.volumeActivity`),
        value: t(`${km}.volumeActivityValue`, { ratio: volumeRatio.toFixed(1) }),
        context: t(`${km}.relativeToRecentAverage`),
        type: 'volume'
      });
    }
    if (price_action.trend_state) {
      keyMetrics.push({
        label: t(`${km}.trendState`),
        value: price_action.trend_state,
        context: t(`${km}.shortTermTrendClassification`),
        type: 'price'
      });
    }
    if (price_action.volatility_state) {
      keyMetrics.push({
        label: t(`${km}.volatilityState`),
        value: price_action.volatility_state,
        context: t(`${km}.currentVolatilityRegime`),
        type: 'price'
      });
    }

    // News metrics
    if (typeof news.article_count === 'number') {
      keyMetrics.push({
        label: t(`${km}.newsCoverage`),
        value: t(`${km}.articleCount`, { count: news.article_count }),
        context: t(`${km}.recentPublicMentions`),
        type: 'news'
      });
    }
    if (typeof news.avg_sentiment_score === 'number') {
      keyMetrics.push({
        label: t(`${km}.newsSentiment`),
        value: formatNumber(news.avg_sentiment_score, 2, t),
        context: t(`${km}.averageFromAnalyzedArticles`),
        type: 'news'
      });
    }

    // Social metrics
    if (typeof social.post_count === 'number') {
      keyMetrics.push({
        label: t(`${km}.communityPosts`),
        value: t(`${km}.postCount`, { count: social.post_count }),
        context: t(`${km}.platformActivityLevel`),
        type: 'social'
      });
    }
    if (typeof social.bull_bear_ratio === 'number') {
      keyMetrics.push({
        label: t(`${km}.bullBearRatio`),
        value: formatNumber(social.bull_bear_ratio, 2, t),
        context: t(`${km}.sentimentBalance`),
        type: 'social'
      });
    }

    // Filing metrics
    if (filings.last_filing?.form) {
      keyMetrics.push({
        label: t(`${km}.lastFiling`),
        value: (
          <span className="flex items-center gap-1">
            {filings.last_filing.form}
            <InfoTooltip content={getFilingTooltip(filings.last_filing.form, t)} />
          </span>
        ),
        context: filings.last_filing.filing_date ? t(`${km}.filedOn`, { date: filings.last_filing.filing_date }) : t(`${km}.filedRecently`),
        type: 'filing'
      });
    }
    if (typeof filings.last_filing?.price_reaction_pct_1d === 'number') {
      keyMetrics.push({
        label: t(`${km}.oneDayFilingReaction`),
        value: formatPercentValue(filings.last_filing.price_reaction_pct_1d, 2, t),
        context: t(`${km}.priceMoveAfterFiling`),
        type: 'filing'
      });
    }

    // Recent activity
    const ra = 'dashboard.expandableStockCard.recentActivityDescriptions';
    const recentActivity = recent_events.slice(0, 3).map(evt => ({
      type: evt.event_type,
      description: evt.event_type === 'filing' ? t(`${ra}.filingPublished`, { form: evt.form }) :
                   evt.event_type === 'news' ? t(`${ra}.newsArticlePublished`) :
                   evt.event_type === 'sentiment' ? t(`${ra}.sentimentShiftDetected`) :
                   t(`${ra}.marketActivityDetected`),
      timestamp: evt.published_at || evt.created_at,
      impact: evt.impact_score ?? 5
    }));

    // Contextual insights
    const cin = 'dashboard.expandableStockCard.contextualInsights';
    const contextualInsights = [];
    if (typeof price_action.change_pct_1d === 'number' && typeof volumeRatio === 'number') {
      if (Math.abs(price_action.change_pct_1d) > 2 && volumeRatio > 1.5) {
        contextualInsights.push({
          type: 'correlation',
          insight: t(`${cin}.priceOnElevatedVolume`),
          explanation: t(`${cin}.priceOnElevatedVolumeExplanation`)
        });
      } else if (Math.abs(price_action.change_pct_1d) > 2 && volumeRatio < 0.8) {
        contextualInsights.push({
          type: 'divergence',
          insight: t(`${cin}.priceOnLowerVolume`),
          explanation: t(`${cin}.priceOnLowerVolumeExplanation`)
        });
      }
    }

    if (typeof news.article_count === 'number' && typeof social.post_count === 'number') {
      if (news.article_count > 3 && social.post_count > 10) {
        contextualInsights.push({
          type: 'attention',
          insight: t(`${cin}.highCoverageWithDiscussion`),
          explanation: t(`${cin}.highCoverageWithDiscussionExplanation`)
        });
      }
    }

    if (filings.last_filing?.form) {
      const form = filings.last_filing.form.toUpperCase();
      if (['10-K', '10-Q', '8-K'].includes(form)) {
        contextualInsights.push({
          type: 'regulatory',
          insight: t(`${cin}.recentFilingOfficialInfo`, { form }),
          explanation: t(`${cin}.recentFilingOfficialInfoExplanation`)
        });
      }
      if (filings.last_filing.summary_bullets?.length) {
        contextualInsights.push({
          type: 'filing_summary',
          insight: t(`${cin}.keyPointsFromLastFiling`),
          explanation: filings.last_filing.summary_bullets.join(' ')
        });
      }
    }

    // Summary
    const sg = 'dashboard.expandableStockCard.summaryGeneration';
    let summary = overall.summary || '';
    if (!summary) {
      summary = `${stock.ticker || stock.symbol || ''} `;
      if (typeof price_action.change_pct_1d === 'number') {
        const dir = price_action.change_pct_1d > 0 ? t(`${sg}.rose`) : price_action.change_pct_1d < 0 ? t(`${sg}.fell`) : t(`${sg}.wasFlat`);
        summary += t(`${sg}.changePctSuffix`, { direction: dir, pct: Math.abs(price_action.change_pct_1d).toFixed(2) });
      }
      if (typeof volumeRatio === 'number') {
        const volDesc = volumeRatio > 1.5 ? t(`${sg}.higherThanTypical`) : volumeRatio < 0.7 ? t(`${sg}.lowerThanTypical`) : t(`${sg}.closeToTypical`);
        summary += t(`${sg}.onVolumeSuffix`, { volDesc });
      }
      if (news.article_count || social.post_count) {
        summary += t(`${sg}.recentlyThereWere`);
        const acts = [];
        if (news.article_count > 0) acts.push(t(`${sg}.newsMention`, { count: news.article_count }));
        if (social.post_count > 0) acts.push(t(`${sg}.communityPost`, { count: social.post_count }));
        summary += acts.join(' and ') + '.';
      }
      if (!summary.trim()) summary = t(`${sg}.activityBeingCollected`);
    }

    return { summary, keyMetrics, recentActivity, contextualInsights, overall };
  }, [signal, stock, t]);

  // Primary driver
  const primaryDriver = useMemo(() => {
    if (!signal?.rings) return null;
    const pd = 'dashboard.expandableStockCard.primaryDriverLabels';
    if (signal.overall?.primary_driver) {
      const labelMap = { price: t(`${pd}.price`), social: t(`${pd}.social`), news: t(`${pd}.news`), filings: t(`${pd}.filings`) };
      return {
        primary: {
          key: signal.overall.primary_driver,
          label: labelMap[signal.overall.primary_driver] || signal.overall.primary_driver,
          score: null
        },
        all: []
      };
    }
    const candidates = [];
    const rings = signal.rings;
    if (rings.price_action?.change_pct_1d) candidates.push({ key: 'price', label: t(`${pd}.price`), score: Math.abs(rings.price_action.change_pct_1d) });
    if (rings.social?.post_count) candidates.push({ key: 'social', label: t(`${pd}.social`), score: rings.social.post_count });
    if (rings.news?.article_count) candidates.push({ key: 'news', label: t(`${pd}.news`), score: rings.news.article_count });
    if (rings.filings?.filing_count) candidates.push({ key: 'filings', label: t(`${pd}.filings`), score: rings.filings.filing_count });
    if (!candidates.length) return null;
    candidates.sort((a, b) => b.score - a.score);
    return { primary: candidates[0], all: candidates };
  }, [signal, t]);

  // Sentiment summary
  const sentimentSummary = useMemo(() => {
    const agg = sentimentAgg || {};
    const sentimentEvents = (contextEvents || []).filter(e => e.event_type === 'sentiment');
    const credible = agg.credible || {};
    const retail = agg.retail || {};
    const noise = agg.noise || {};
    const groupsMembership = agg.groups_membership || {};
    const groupsPosts = agg.groups_posts || {};

    const overallLabel = credible.combined_sentiment || retail.combined_sentiment;
    const overallScore = credible.combined_score ?? retail.combined_score;
    const overallConfidence = credible.confidence ?? retail.confidence;
    const timeframe = agg.timeframe || 'all';
    const lastUpdated = agg.last_updated || agg.lastUpdated;

    const makeBlock = (block, label) => {
      if (!block) return null;
      const votes = block.votes || {};
      return {
        label,
        combined_sentiment: block.combined_sentiment,
        combined_score: block.combined_score,
        reliability: block.reliability,
        confidence: block.confidence,
        divergence: block.divergence,
        no_data: block.no_data,
        votes: {
          bullish_count: votes.bullish_count || 0,
          bearish_count: votes.bearish_count || 0,
          total_votes: votes.total_votes || 0,
          bullish_ratio: votes.bullish_ratio,
          bearish_ratio: votes.bearish_ratio
        },
        news: block.news ? { articles_analyzed: block.news.articles_analyzed || 0, avg_score: block.news.avg_score } : null
      };
    };

    const blocks = [
      makeBlock(retail, 'Retail (all users)'),
      makeBlock(credible, 'Credible (score ≥ threshold)'),
      makeBlock(noise, 'Noise (low-cred)'),
      makeBlock(groupsMembership, 'Your Groups (membership)'),
      makeBlock(groupsPosts, 'Your Groups (posts)')
    ].filter(Boolean);

    return { overallLabel, overallScore, overallConfidence, timeframe, lastUpdated, blocks, sentimentEvents };
  }, [sentimentAgg, contextEvents]);

  // Event handlers
  const handlePostAboutEvent = (evt) => {
    setComposerContext({ type: 'event', evt, prefill: generateEventPrefill(evt) });
    setShowPostComposer(true);
    setActiveTab('post');
  };

  const handlePostAboutChart = () => {
    setComposerContext({ type: 'chart', interpretation: chartInterpretation, prefill: generateChartPrefill(chartInterpretation) });
    setShowPostComposer(true);
    setActiveTab('post');
  };

  const handlePostAboutSummary = () => {
    setComposerContext({ type: 'summary', summary: stockSummary, prefill: generateSummaryPrefill(stockSummary) });
    setShowPostComposer(true);
    setActiveTab('post');
  };

  const handleExport = () => {
    const data = {
      ticker: stock.ticker || stock.symbol,
      timestamp: new Date().toISOString(),
      summary: stockSummary.summary,
      keyMetrics: stockSummary.keyMetrics,
      sentiment: sentimentSummary,
      events: contextEvents.slice(0, 10),
      chartInterpretation
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${stock.ticker || stock.symbol}-analysis-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    if (onExport) onExport(data);
  };

  const generateEventPrefill = (evt) => {
    if (evt.suggested_post_stub) return evt.suggested_post_stub;
    const ep = 'dashboard.expandableStockCard.eventPrefill';
    const parts = [t(`${ep}.justSaw`, { ticker: stock.ticker || stock.symbol })];
    if (evt.event_type === 'filing') {
      parts.push(t(`${ep}.filedForm`, { form: evt.form }));
    } else if (evt.event_type === 'news') {
      parts.push(t(`${ep}.inTheNews`));
      if (evt.headline) parts.push(t(`${ep}.headlineQuote`, { headline: evt.headline.substring(0, 80) }));
    } else if (evt.event_type === 'sentiment') {
      parts.push(t(`${ep}.sentimentShift`));
    } else if (evt.event_type === 'abnormal') {
      parts.push(t(`${ep}.abnormalActivity`));
    }
    parts.push('\n\n');
    return parts.join(' ');
  };

  const generateChartPrefill = (interpretation) => {
    return `${stock.ticker || stock.symbol} ${interpretation.summary.toLowerCase()}. ${interpretation.details[0] || ''}\n\n`;
  };

  const generateSummaryPrefill = (summary) => {
    return `${summary.summary}\n\n${t('dashboard.expandableStockCard.interestingToSee', { insight: summary.contextualInsights[0]?.insight || t('dashboard.expandableStockCard.thisActivityPattern') })}`;
  };

  // ✅ COMPLETE Redux data mapping with extensive fallbacks
  // ================================
  // CANONICAL PRICE DERIVATION
  // ================================

  const snapshot =
    stock.marketSnapshot ||
    stock.snapshot ||
    null;

  let displayPrice = null;
  let priceChange = null;
  let percentChange = null;
  let priceSource = 'none';

  // 1️⃣ Market snapshot (authoritative)
  if (
    snapshot &&
    typeof snapshot.last_price === 'number' &&
    typeof snapshot.prev_close === 'number' &&
    snapshot.prev_close !== 0
  ) {
    displayPrice = snapshot.last_price;
    priceChange = snapshot.last_price - snapshot.prev_close;
    percentChange = (priceChange / snapshot.prev_close) * 100;
    priceSource = 'market_snapshot';
  }

  // 2️⃣ Backend already computed percent
  else if (
    typeof stock.changePercent === 'number' &&
    typeof stock.lastPrice === 'number'
  ) {
    displayPrice = stock.lastPrice;
    percentChange = stock.changePercent;
    priceChange = (percentChange / 100) * displayPrice;
    priceSource = 'backend_percent';
  }

  // 3️⃣ Explicit prev_close fallback
  else if (
    typeof stock.lastPrice === 'number' &&
    typeof stock.prev_close === 'number' &&
    stock.prev_close !== 0
  ) {
    displayPrice = stock.lastPrice;
    priceChange = stock.lastPrice - stock.prev_close;
    percentChange = (priceChange / stock.prev_close) * 100;
    priceSource = 'prev_close_fallback';
  }

  // 4️⃣ Legacy / Yahoo-style feeds
  else if (
    typeof stock.regularMarketPrice === 'number' &&
    typeof stock.regularMarketPreviousClose === 'number' &&
    stock.regularMarketPreviousClose !== 0
  ) {
    displayPrice = stock.regularMarketPrice;
    priceChange = stock.regularMarketPrice - stock.regularMarketPreviousClose;
    percentChange = (priceChange / stock.regularMarketPreviousClose) * 100;
    priceSource = 'legacy_market';
  }

  // 5️⃣ Last-resort price only (NO % FABRICATION)
  else if (typeof stock.currentPrice === 'number') {
    displayPrice = stock.currentPrice;
    priceSource = 'price_only';
  }



  const displaySymbol = 
    stock.ticker || 
    stock.symbol || 
    stock.s || 
    stock.t ||
    'N/A';

  const displayName = 
    stock.name || 
    stock.company_name || 
    stock.companyName || 
    stock.longName || 
    stock.shortName ||
    'Unknown Company';

  const displayVolume = 
    stock.volume || 
    stock.vol || 
    stock.v || 
    stock.regularMarketVolume ||
    '0';

  return (
    <div 
      ref={cardRef}
      className={`
        w-full max-w-full break-words overflow-hidden
        bg-cream-100 dark:bg-slate-900/60 border rounded-xl
        transition-all duration-300 shadow-lg
        ${isExpanded
          ? 'border-indigo-500/60 shadow-indigo-500/10 col-span-full'
          : 'border-gray-200 dark:border-slate-800/60 hover:border-gray-300 dark:hover:border-slate-700/80'
        }
      `}
    >
      {/* COLLAPSED VIEW */}
      <div onClick={onToggle} className="p-4 cursor-pointer hover:bg-cream-900/5 dark:hover:bg-slate-800/30 transition-colors">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-start gap-2.5 flex-1 min-w-0">
            <StockLogo symbol={displaySymbol} name={displayName} size="w-9 h-9" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap min-w-0">
                {/* 2026-08-11: this was the one text node in this row missing
                    a truncate guard (its displayName sibling below already
                    has one) -- justify-between/ml-3 on the price container
                    only position the two flex children, they don't clip
                    overflow, so a squeezed card (2-up grid at 768px minus
                    sidebar) let the ticker's own text overflow past its
                    shrunk flex-item edge and visually collide with the
                    price. min-w-0 lets it actually shrink to truncate
                    instead of being held to its intrinsic content width. */}
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 truncate min-w-0 max-w-full">{displaySymbol}</h3>
                {isExpanded && (
                  <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 rounded border border-indigo-500/20">
                    {t('dashboard.expandableStockCard.collapsed.focusMode')}
                  </span>
                )}
                {recencyTimeline.last24h.length > 0 && (
                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded border border-amber-500/20 animate-pulse">
                    <Clock className="w-3 h-3" />
                    {t('dashboard.expandableStockCard.collapsed.newCount', { count: recencyTimeline.last24h.length })}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{displayName}</p>
            </div>
          </div>
          
          <div className="text-right flex-shrink-0 ml-3">
            <div className="flex flex-wrap items-center gap-1 justify-end mb-1">
              <span className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                {typeof displayPrice === 'number' ? `$${displayPrice.toFixed(2)}` : '—'}
              </span>

              {typeof percentChange === 'number' && (
                percentChange >= 0
                  ? <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  : <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
            </div>

            {typeof percentChange === 'number' ? (
              <span
                className={`text-sm font-semibold ${
                  percentChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}
              >
                {percentChange >= 0 ? '+' : ''}
                {percentChange.toFixed(2)}%
              </span>
            ) : (
              <span className="text-sm font-semibold text-gray-500 dark:text-slate-500">—</span>
            )}
          </div>

        </div>

        {/* Sparkline — passes raw sparklineData; the component normalises all known shapes */}
        {!isExpanded && stock.sparklineData != null && (
          <div className="w-full overflow-hidden mb-3 -mx-2 px-2">
            <Sparkline
              data={stock.sparklineData}
              height={60}
              className="w-full"
              debugLabel={stock.ticker}
            />
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs">
            <BarChart3 className="w-3.5 h-3.5 text-gray-500 dark:text-slate-400" />
            <span className="text-gray-500 dark:text-slate-500">{t('dashboard.expandableStockCard.collapsed.vol')}</span>
            <span className="text-gray-600 dark:text-slate-300 font-medium">{Number(displayVolume).toLocaleString()}</span>
          </div>
          {stock.hasNews && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 rounded border border-purple-500/20">
              <Newspaper className="w-3 h-3" />{t('dashboard.expandableStockCard.collapsed.news')}
            </span>
          )}
          {stock.hasFilings && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 rounded border border-blue-500/20">
              <FileText className="w-3 h-3" />{t('dashboard.expandableStockCard.collapsed.filing')}
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          >
            {isExpanded ? (
              <><span>{t('dashboard.expandableStockCard.collapsed.collapse')}</span><ChevronUp className="w-4 h-4" /></>
            ) : (
              <><span>{t('dashboard.expandableStockCard.collapsed.focus')}</span><ChevronDown className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>

      {/* EXPANDED VIEW */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-slate-800/60">
          {/* STICKY ACTIONS (FIXED - only when not in post composer) */}
          {!showPostComposer && showStickyActions && (
            <div className="sticky top-0 z-20 bg-white dark:bg-slate-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-slate-800/60 p-2 flex gap-2">
              <button onClick={() => setActiveTab('chart')} className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/60 dark:hover:bg-slate-800/80 rounded text-xs text-gray-600 dark:text-slate-300 transition-all flex items-center justify-center gap-1">
                <BarChart3 className="w-3 h-3" /><span className="hidden sm:inline">{t('dashboard.expandableStockCard.stickyActions.chart')}</span>
              </button>
              <button onClick={() => setActiveTab('summary')} className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/60 dark:hover:bg-slate-800/80 rounded text-xs text-gray-600 dark:text-slate-300 transition-all flex items-center justify-center gap-1">
                <Target className="w-3 h-3" /><span className="hidden sm:inline">{t('dashboard.expandableStockCard.stickyActions.summary')}</span>
              </button>
              <button onClick={() => setActiveTab('post')} className="flex-1 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 rounded text-xs text-blue-700 dark:text-blue-300 transition-all flex items-center justify-center gap-1">
                <MessageSquare className="w-3 h-3" /><span className="hidden sm:inline">{t('dashboard.expandableStockCard.stickyActions.post')}</span>
              </button>
              {onSetAlert && (
                <button onClick={() => onSetAlert(stock)} className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/60 dark:hover:bg-slate-800/80 rounded text-xs text-gray-600 dark:text-slate-300 transition-all flex items-center justify-center gap-1">
                  <Bell className="w-3 h-3" /><span className="hidden sm:inline">{t('dashboard.expandableStockCard.stickyActions.alert')}</span>
                </button>
              )}
              <button onClick={handleExport} className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/60 dark:hover:bg-slate-800/80 rounded text-xs text-gray-600 dark:text-slate-300 transition-all flex items-center justify-center gap-1">
                <Download className="w-3 h-3" /><span className="hidden sm:inline">{t('dashboard.expandableStockCard.stickyActions.export')}</span>
              </button>
            </div>
          )}

          {/* TAB NAVIGATION */}
          <div className="flex items-center gap-1 p-2 bg-cream-100 dark:bg-slate-800/30 border-b border-gray-200 dark:border-slate-800/40 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800/50 snap-x snap-mandatory">
            <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={<Activity className="w-3.5 h-3.5" />} label={t('dashboard.expandableStockCard.tabs.overview')} kbd="1" />
            <TabButton active={activeTab === 'summary'} onClick={() => setActiveTab('summary')} icon={<Target className="w-3.5 h-3.5" />} label={t('dashboard.expandableStockCard.tabs.summary')} badge={stockSummary.keyMetrics.length} kbd="2" />
            <TabButton active={activeTab === 'sentiment'} onClick={() => setActiveTab('sentiment')} icon={<TrendingUp className="w-3.5 h-3.5" />} label={t('dashboard.expandableStockCard.tabs.sentiment')} badge={sentimentSummary.sentimentEvents.length} kbd="3" />
            <TabButton active={activeTab === 'chart'} onClick={() => setActiveTab('chart')} icon={<BarChart3 className="w-3.5 h-3.5" />} label={t('dashboard.expandableStockCard.tabs.chart')} kbd="4" />
            <TabButton active={activeTab === 'context'} onClick={() => setActiveTab('context')} icon={<Calendar className="w-3.5 h-3.5" />} label={t('dashboard.expandableStockCard.tabs.events')} pulse={recencyTimeline.last24h.length > 0} kbd="5" />
            <TabButton active={activeTab === 'post'} onClick={() => setActiveTab('post')} icon={<MessageSquare className="w-3.5 h-3.5" />} label={t('dashboard.expandableStockCard.tabs.post')} kbd="P" />
          </div>

          {/* TAB CONTENT */}
          <div ref={contentRef} className="p-3 sm:p-4 space-y-4 max-h-[60vh] lg:max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800/50">
            
            {/* OVERVIEW TAB - COMPLETE */}
            {activeTab === 'overview' && (
              <OverviewTab 
                recencyTimeline={recencyTimeline}
                signal={signal}
                recentPosts={recentPosts}
                stockSummary={stockSummary}
                primaryDriver={primaryDriver}
                chartInterpretation={chartInterpretation}
                currentTimeFrame={currentTimeFrame}
                setActiveTab={setActiveTab}
              />
            )}

            {/* SUMMARY TAB - COMPLETE */}
            {activeTab === 'summary' && (
              <SummaryTab
                stockSummary={stockSummary}
                handlePostAboutSummary={handlePostAboutSummary}
                displaySymbol={displaySymbol}
              />
            )}

            {/* SENTIMENT TAB - COMPLETE WITH EDUCATION BANNER */}
            {activeTab === 'sentiment' && (
              <SentimentTab
                sentimentSummary={sentimentSummary}
                sentimentAgg={sentimentAgg}
                handlePostAboutEvent={handlePostAboutEvent}
                handlePostAboutChart={handlePostAboutChart}
              />
            )}

            {/* CHART TAB - COMPLETE */}
            {activeTab === 'chart' && (
              <ChartTab
                chartInterpretation={chartInterpretation}
                technicalContext={technicalContext}
                handlePostAboutChart={handlePostAboutChart}
                chartData={chartData}
                indicators={indicators}
                currentTimeFrame={currentTimeFrame}
                currentIndicators={currentIndicators}
                onTimeFrameChange={onTimeFrameChange}
                onIndicatorsChange={onIndicatorsChange}
              />
            )}

            {/* EVENTS TAB - COMPLETE */}
            {activeTab === 'context' && (
              <EventsTab
                recencyTimeline={recencyTimeline}
                contextEvents={contextEvents}
                handlePostAboutEvent={handlePostAboutEvent}
              />
            )}

            {/* POST TAB - COMPLETE */}
            {activeTab === 'post' && (
              <PostTab
                displaySymbol={displaySymbol}
                stock={stock}
                composerContext={composerContext}
                onCreatePost={onCreatePost}
                setShowPostComposer={setShowPostComposer}
                setComposerContext={setComposerContext}
                contextEvents={contextEvents}
                handlePostAboutEvent={handlePostAboutEvent}
                handlePostAboutChart={handlePostAboutChart}
                showPostComposer={showPostComposer}
                // FIX: pass onClose so the composer X button can close itself
                onClose={() => {
                  setShowPostComposer(false);
                  setComposerContext(null);
                }}
              />
            )}

          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.stock.ticker === nextProps.stock.ticker &&
    prevProps.stock.currentPrice === nextProps.stock.currentPrice &&
    prevProps.stock.sparklineData === nextProps.stock.sparklineData &&  // ← ADD THIS
    JSON.stringify(prevProps.contextEvents) === JSON.stringify(nextProps.contextEvents) &&
    prevProps.isLoading === nextProps.isLoading &&
    JSON.stringify(prevProps.signal) === JSON.stringify(nextProps.signal) &&
    JSON.stringify(prevProps.sentimentAgg) === JSON.stringify(nextProps.sentimentAgg)
  );
});

ExpandableStockCard.displayName = 'ExpandableStockCard';

// === TAB COMPONENTS (COMPLETE IMPLEMENTATIONS) ===

const TabButton = ({ active, onClick, icon, label, badge, kbd, pulse }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 snap-start px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap relative ${
      active ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-500/30' : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-700/30'
    }`}
  >
    {icon}
    <span className="ml-1">{label}</span>
    {badge > 0 && <span className="ml-1 px-1.5 py-0.5 bg-blue-600/30 text-blue-200 text-[10px] rounded">{badge}</span>}
    {pulse && <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full animate-pulse" />}
    {kbd && <kbd className="hidden sm:inline ml-2 px-1.5 py-0.5 bg-cream-100 dark:bg-slate-900/60 rounded text-[10px]">{kbd}</kbd>}
  </button>
);

const OverviewTab = ({ recencyTimeline, signal, recentPosts, stockSummary, primaryDriver, chartInterpretation, currentTimeFrame, setActiveTab }) => {
  const { t } = useTranslation();
  const ov = 'dashboard.expandableStockCard.overviewTab';
  const na = t('dashboard.expandableStockCard.na');
  return (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <StatCard icon={<Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />} label={t(`${ov}.last24Hours`)} value={t(`${ov}.eventCount`, { count: recencyTimeline.last24h.length })} />
      <StatCard icon={<Users className="w-4 h-4 text-green-600 dark:text-green-400" />} label={t(`${ov}.discussion`)} value={t(`${ov}.postsCount`, { count: signal?.rings?.social?.post_count ?? recentPosts.length ?? 0 })} />
      <StatCard icon={<Newspaper className="w-4 h-4 text-purple-600 dark:text-purple-400" />} label={t(`${ov}.coverage`)} value={t(`${ov}.articlesCount`, { count: signal?.rings?.news?.article_count ?? 0 })} />
    </div>

    {signal?.overall && (
      <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-slate-200 flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />{t(`${ov}.stockerSignalOverall`)}
        </h5>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label={t(`${ov}.direction`)} value={signal.overall.direction || na} icon={<TrendingUp className="w-4 h-4" />} />
          <StatCard label={t(`${ov}.score`)} value={typeof signal.overall.score === 'number' ? signal.overall.score.toFixed(2) : na} icon={<Percent className="w-4 h-4" />} />
          <StatCard label={t(`${ov}.confidence`)} value={typeof signal.overall.confidence === 'number' ? `${Math.round(signal.overall.confidence <= 1 ? signal.overall.confidence * 100 : signal.overall.confidence)}%` : na} icon={<Info className="w-4 h-4" />} />
          <StatCard label={t(`${ov}.primaryDriver`)} value={signal.overall.primary_driver || t(`${ov}.mixed`)} icon={<Target className="w-4 h-4" />} />
        </div>
        {signal.overall.summary && <p className="text-xs text-gray-600 dark:text-slate-300 mt-3">{signal.overall.summary}</p>}
      </div>
    )}

    {primaryDriver && (
      <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-slate-200 flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />{t(`${ov}.primaryMarketDriver`)}
        </h5>
        <div className="bg-cream-100 dark:bg-slate-900/40 border border-cream-300 dark:border-slate-700/50 rounded-lg px-3 py-2 inline-block">
          <p className="text-sm font-semibold text-gray-900 dark:text-slate-200">{primaryDriver.primary.label}</p>
        </div>
      </div>
    )}

    {chartInterpretation.summary && (
      <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-slate-200 flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />{t(`${ov}.priceTrendLast`, { timeframe: currentTimeFrame })}
        </h5>
        <p className="text-sm text-gray-600 dark:text-slate-300 mb-3">{chartInterpretation.summary}</p>
        <div className="space-y-1">
          {chartInterpretation.details.map((d, i) => (
            <p key={i} className="text-xs text-gray-500 dark:text-slate-500 flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span><span>{d}</span>
            </p>
          ))}
        </div>
        <p className="text-[10px] text-gray-500 dark:text-slate-500 mt-3 pt-3 border-t border-cream-300 dark:border-slate-700/40">{t(`${ov}.descriptiveDisclaimer`)}</p>
      </div>
    )}

    {signal && (
      <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-slate-200 flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-purple-600 dark:text-purple-400" />{t(`${ov}.marketActivityByRing`)}
        </h5>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {signal.rings?.social && <StatCard label={t(`${ov}.socialPosts`)} value={signal.rings.social.post_count || 0} icon={<Users className="w-4 h-4" />} />}
          {signal.rings?.news && <StatCard label={t(`${ov}.newsArticles`)} value={signal.rings.news.article_count || 0} icon={<Newspaper className="w-4 h-4" />} />}
          {signal.rings?.filings && <StatCard label={t(`${ov}.recentFilings`)} value={signal.rings.filings.filing_count || 0} icon={<FileText className="w-4 h-4" />} />}
          {signal.rings?.price_action && <StatCard label={t(`${ov}.abnormalEvents`)} value={signal.rings.price_action.abnormal_events?.length || 0} icon={<AlertCircle className="w-4 h-4" />} />}
        </div>
      </div>
    )}

    <div className="flex gap-2">
      <button onClick={() => setActiveTab('summary')} className="flex-1 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-sm text-blue-700 dark:text-blue-300 transition-all flex items-center justify-center gap-2">
        <Target className="w-4 h-4" /><span className="hidden sm:inline">{t(`${ov}.viewSummary`)}</span><span className="sm:hidden">{t(`${ov}.summaryShort`)}</span>
      </button>
      <button onClick={() => setActiveTab('context')} className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800/60 dark:hover:bg-slate-800/80 border border-cream-300 dark:border-slate-700/40 rounded-lg text-sm text-gray-600 dark:text-slate-300 transition-all flex items-center justify-center gap-2">
        <Calendar className="w-4 h-4" /><span className="hidden sm:inline">{t(`${ov}.viewEvents`)}</span><span className="sm:hidden">{t(`${ov}.eventsShort`)}</span>
      </button>
    </div>
  </>
  );
};

const SummaryTab = ({ stockSummary, handlePostAboutSummary, displaySymbol }) => {
  const { t } = useTranslation();
  const st = 'dashboard.expandableStockCard.summaryTab';
  return (
  <>
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-slate-200 mb-1">{t(`${st}.activitySummary`)}</h3>
        <p className="text-xs text-gray-500 dark:text-slate-400">{t(`${st}.subtitle`)}</p>
      </div>
      <button onClick={handlePostAboutSummary} className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-xs text-blue-700 dark:text-blue-300 transition-all flex items-center gap-1">
        <MessageSquare className="w-3 h-3" />{t(`${st}.postAboutThis`)}
      </button>
    </div>

    <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4">
      <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">{stockSummary.summary}</p>
    </div>

    {stockSummary.keyMetrics.length > 0 && (
      <div>
        <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-500 dark:text-slate-400" />{t(`${st}.keyMetricsHeading`)}
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {stockSummary.keyMetrics.map((m, i) => (
            <div key={i} className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
              <div className="text-xs text-gray-500 dark:text-slate-500 mb-1">{m.label}</div>
              <div className="text-base font-bold text-gray-900 dark:text-slate-200 mb-1">{m.value}</div>
              <div className="text-xs text-gray-500 dark:text-slate-400">{m.context}</div>
            </div>
          ))}
        </div>
      </div>
    )}

    {stockSummary.recentActivity.length > 0 && (
      <div>
        <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500 dark:text-slate-400" />{t(`${st}.recentActivity`)}
        </h4>
        <div className="space-y-2">
          {stockSummary.recentActivity.map((a, i) => (
            <div key={i} className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
              <div className="flex items-start justify-between mb-1">
                <span className="text-xs font-medium text-gray-600 dark:text-slate-300 capitalize flex items-center gap-2">
                  {a.type === 'filing' && <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" />}
                  {a.type === 'news' && <Newspaper className="w-3 h-3 text-purple-600 dark:text-purple-400" />}
                  {a.type === 'sentiment' && <TrendingUp className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />}
                  {a.description}
                </span>
                <span className="text-xs text-gray-500 dark:text-slate-500">{formatTimeAgo({ published_at: a.timestamp }, t)}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-gray-500 dark:text-slate-500">{t(`${st}.impact`)}</span>
                <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700/50 rounded-full overflow-hidden max-w-[100px]">
                  <div className={`h-full ${a.impact > 7 ? 'bg-red-500' : a.impact > 5 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${(a.impact / 10) * 100}%` }} />
                </div>
                <span className="text-xs text-gray-500 dark:text-slate-400">{a.impact.toFixed(1)}/10</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {stockSummary.contextualInsights.length > 0 && (
      <div>
        <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-gray-500 dark:text-slate-400" />{t(`${st}.contextualInsights`)}
        </h4>
        <div className="space-y-2">
          {stockSummary.contextualInsights.map((ins, i) => (
            <div key={i} className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
              <div className="text-sm text-gray-600 dark:text-slate-300 font-medium mb-1">{ins.insight}</div>
              <div className="text-xs text-gray-500 dark:text-slate-400">{ins.explanation}</div>
            </div>
          ))}
        </div>
      </div>
    )}

    {stockSummary.keyMetrics.length === 0 && stockSummary.recentActivity.length === 0 && stockSummary.contextualInsights.length === 0 && (
      <div className="text-center py-8">
        <Activity className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-slate-400 text-sm">{t(`${st}.dataBeingCollected`)}</p>
        <p className="text-gray-500 dark:text-slate-500 text-xs mt-1">{t(`${st}.checkBackSoon`)}</p>
      </div>
    )}

    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
      <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">
        <strong className="text-amber-600 dark:text-amber-400">{t(`${st}.importantLabel`)}</strong> {t(`${st}.importantDisclaimer`)}
      </p>
    </div>
  </>
  );
};

const SentimentTab = ({ sentimentSummary, sentimentAgg, handlePostAboutEvent, handlePostAboutChart }) => {
  const { t } = useTranslation();
  const se = 'dashboard.expandableStockCard.sentimentTab';
  const na = t('dashboard.expandableStockCard.na');
  return (
  <>
    {/* COMPLIANCE FIX: Education banner at top */}
    <div className="bg-blue-100 dark:bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 text-xs text-gray-600 dark:text-slate-300 leading-relaxed">
          <p className="font-semibold text-blue-700 dark:text-blue-300 mb-1">{t(`${se}.aboutTitle`)}</p>
          <p>{t(`${se}.aboutBody`, { confidenceBold: t(`${se}.confidenceBold`), descriptiveBold: t(`${se}.descriptiveOnlyBold`) })}</p>
        </div>
      </div>
    </div>

    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-slate-200 mb-1">{t(`${se}.sentimentOverview`)}</h3>
        <p className="text-xs text-gray-500 dark:text-slate-400">{t(`${se}.subtitle`)}</p>
        <p className="text-[10px] text-gray-500 dark:text-slate-500 mt-1">
          {t(`${se}.timeframeLabel`, { timeframe: sentimentSummary.timeframe || 'all' })} •{' '}
          {sentimentSummary.lastUpdated && t(`${se}.lastUpdatedLabel`, { date: new Date(sentimentSummary.lastUpdated).toLocaleString() })}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (sentimentSummary.sentimentEvents[0]) handlePostAboutEvent(sentimentSummary.sentimentEvents[0]);
          else handlePostAboutChart();
        }}
        className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-xs text-blue-700 dark:text-blue-300 transition-all flex items-center gap-1"
      >
        <MessageSquare className="w-3 h-3" />{t(`${se}.postAboutSentiment`)}
      </button>
    </div>

    <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4 mb-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">{t(`${se}.credibilityWeightedSentiment`)}</h4>
      <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed mb-3">
        {sentimentSummary.overallLabel
          ? t(`${se}.leanSentence`, {
              label: sentimentSummary.overallLabel.replace('_', ' '),
              confidencePart: typeof sentimentSummary.overallConfidence === 'number'
                ? t(`${se}.confidenceAround`, { pct: Math.round((sentimentSummary.overallConfidence <= 1 ? sentimentSummary.overallConfidence * 100 : sentimentSummary.overallConfidence)) })
                : t(`${se}.availableDataConfidence`)
            })
          : t(`${se}.stillForming`)}
      </p>

      {sentimentAgg?.credible?.divergence && (
        <div className="bg-amber-100 dark:bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 rounded-lg px-3 py-2 text-xs mb-3">
          {t(`${se}.divergenceWarning`)}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-3">
        <StatCard label={t(`${se}.credibleScore`)} value={typeof sentimentAgg?.credible?.combined_score === 'number' ? sentimentAgg.credible.combined_score.toFixed(2) : na} icon={<TrendingUp className="w-4 h-4" />} />
        <StatCard label={t(`${se}.retailScore`)} value={typeof sentimentAgg?.retail?.combined_score === 'number' ? sentimentAgg.retail.combined_score.toFixed(2) : na} icon={<Users className="w-4 h-4" />} />
        <StatCard label={t(`${se}.noiseScore`)} value={typeof sentimentAgg?.noise?.combined_score === 'number' ? sentimentAgg.noise.combined_score.toFixed(2) : na} icon={<AlertCircle className="w-4 h-4" />} />
      </div>

      <div className="space-y-2">
        {['credible','retail','noise'].map(key => {
          const block = sentimentAgg?.[key];
          if (!block) return null;
          const total = block?.votes?.total_votes || 0;
          const bull = block?.votes?.bullish_count || 0;
          const bear = block?.votes?.bearish_count || 0;
          const bullPct = total ? (bull/total)*100 : 0;
          const bearPct = total ? (bear/total)*100 : 0;
          return (
            <div key={key}>
              <div className="text-[11px] text-gray-500 dark:text-slate-400 mb-1 capitalize">{t(`${se}.votesLabel`, { key, count: total })}</div>
              <div className="flex h-2 w-full rounded overflow-hidden bg-gray-200 dark:bg-slate-700/60">
                <div className="bg-emerald-500" style={{width:`${bullPct}%`}} />
                <div className="bg-red-500" style={{width:`${bearPct}%`}} />
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {sentimentSummary.blocks.length > 0 && (
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3">{t(`${se}.breakdownByCohort`)}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sentimentSummary.blocks.map((block, idx) => {
            const totalVotes = block.votes.total_votes || 0;
            const bullish = block.votes.bullish_count || 0;
            const bearish = block.votes.bearish_count || 0;
            const bullishRatio = block.votes.bullish_ratio ?? (totalVotes > 0 ? bullish / totalVotes : 0);
            const bearishRatio = block.votes.bearish_ratio ?? (totalVotes > 0 ? bearish / totalVotes : 0);

            return (
              <div key={idx} className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs font-semibold text-gray-900 dark:text-slate-200">{block.label}</div>
                    <div className="text-[10px] text-gray-500 dark:text-slate-500">
                      {t(`${se}.reliabilityLabel`, { reliability: block.reliability || na })}
                      {block.divergence && t(`${se}.divergenceSuffix`)}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-500 dark:text-slate-400">
                    {t(`${se}.scoreLabel`, { score: typeof block.combined_score === 'number' ? block.combined_score.toFixed(2) : na })}<br />
                    {t(`${se}.confLabel`, { pct: typeof block.confidence === 'number' ? Math.round(block.confidence <= 1 ? block.confidence * 100 : block.confidence) : na })}
                  </div>
                </div>
                <div className="text-[11px] text-gray-500 dark:text-slate-400 mb-1">{t(`${se}.sentimentLabelLine`, { label: block.combined_sentiment || na })}</div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-gray-500 dark:text-slate-500">{t(`${se}.votesCount`, { count: totalVotes })}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700/50 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, bullishRatio * 100)}%` }} />
                    <div className="h-full bg-red-500 -mt-1.5" style={{ width: `${Math.min(100, bearishRatio * 100)}%` }} />
                  </div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-500 dark:text-slate-500 mb-1">
                  <span>{t(`${se}.bullLabel`, { count: bullish })}</span><span>{t(`${se}.bearLabel`, { count: bearish })}</span>
                </div>
                {block.news && (
                  <div className="mt-2 text-[10px] text-gray-500 dark:text-slate-500">
                    {t(`${se}.newsAnalyzed`, { count: block.news.articles_analyzed || 0, score: typeof block.news.avg_score === 'number' ? block.news.avg_score.toFixed(2) : na })}
                  </div>
                )}
                {block.no_data && <div className="mt-2 text-[10px] text-amber-600 dark:text-amber-400">{t(`${se}.limitedOrNoData`)}</div>}
              </div>
            );
          })}
        </div>
      </div>
    )}

    {sentimentSummary.sentimentEvents.length > 0 ? (
      <div>
        <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500 dark:text-slate-400" />{t(`${se}.recentSentimentEvents`)}
        </h4>
        <div className="space-y-2">
          {sentimentSummary.sentimentEvents.slice(0, 5).map((evt, idx) => {
            const hasScore = typeof evt.sentiment_score === 'number';
            const label = evt.sentiment_label || evt.label;
            return (
              <div key={evt.id || idx} className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-600 dark:text-slate-300 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />{t(`${se}.sentimentShift`)}
                    </span>
                    {label && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream-100 dark:bg-slate-900/60 border border-cream-300 dark:border-slate-700/60 text-gray-900 dark:text-slate-200">{label}</span>}
                  </div>
                  <span className="text-xs text-gray-500 dark:text-slate-500">{formatTimeAgo(evt, t)}</span>
                </div>
                {evt.headline && <p className="text-xs text-gray-600 dark:text-slate-300 mb-1 line-clamp-2">{evt.headline}</p>}
                {evt.summary && <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-1 line-clamp-2">{evt.summary}</p>}
                {hasScore && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-gray-500 dark:text-slate-500">{t(`${se}.sentimentScoreLabel`)}</span>
                    <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700/50 rounded-full overflow-hidden max-w-[120px]">
                      <div className={`h-full ${evt.sentiment_score > 0.2 ? 'bg-emerald-500' : evt.sentiment_score < -0.2 ? 'bg-red-500' : 'bg-slate-400'}`} style={{ width: `${Math.min(100, Math.abs(evt.sentiment_score) * 100)}%` }} />
                    </div>
                    <span className="text-[11px] text-gray-500 dark:text-slate-400">{evt.sentiment_score.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <button onClick={(e) => { e.stopPropagation(); handlePostAboutEvent(evt); }} className="text-xs px-2 py-1 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded transition-colors">
                    <MessageSquare className="w-3 h-3 inline mr-1" />{t(`${se}.postAboutThis`)}
                  </button>
                  {(evt.source_url || evt.filing_url) && (
                    <a href={evt.source_url || evt.filing_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {t(`${se}.viewSource`)}<ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ) : (
      <div className="text-center py-8">
        <TrendingUp className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-slate-400 text-sm">{t(`${se}.noEventsTitle`)}</p>
        <p className="text-gray-500 dark:text-slate-500 text-xs mt-1">{t(`${se}.noEventsBody`)}</p>
      </div>
    )}

    <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
      <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed">{t(`${se}.finalDisclaimer`)}</p>
    </div>
  </>
  );
};

const ChartTab = ({ chartInterpretation, technicalContext, handlePostAboutChart, chartData, indicators, currentTimeFrame, currentIndicators, onTimeFrameChange, onIndicatorsChange }) => {
  const { t } = useTranslation();
  const ct = 'dashboard.expandableStockCard.chartTab';
  return (
  <>
    <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-1">{t(`${ct}.technicalSummary`)}</h4>
          <p className="text-sm text-gray-500 dark:text-slate-400">{chartInterpretation.summary}</p>
          {technicalContext?.trend_label && <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">{t(`${ct}.technicalContextLabel`, { label: technicalContext.trend_label })}</p>}
        </div>
        <button onClick={handlePostAboutChart} className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-xs text-blue-700 dark:text-blue-300 transition-all flex items-center gap-1 flex-shrink-0 ml-3">
          <MessageSquare className="w-3 h-3" />{t(`${ct}.writeAPostAboutThis`)}
        </button>
      </div>
      {chartInterpretation.details.length > 0 && (
        <div className="mt-3 pt-3 border-t border-cream-300 dark:border-slate-700/40">
          <ul className="space-y-1">
            {chartInterpretation.details.map((d, i) => (
              <li key={i} className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-2">
                <div className="w-1 h-1 bg-blue-400 rounded-full flex-shrink-0" /><span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>

    {chartData && chartData.length > 0 ? (
      <StockChart
        data={chartData}
        indicators={indicators}
        currentTimeFrame={currentTimeFrame}
        currentIndicators={currentIndicators}
        onTimeFrameChange={onTimeFrameChange}
        onIndicatorsChange={onIndicatorsChange}
      />
    ) : (
      <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-8 text-center">
        <BarChart3 className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-slate-400 text-sm">{t(`${ct}.chartDataLoading`)}</p>
      </div>
    )}
  </>
  );
};

const EventsTab = ({ recencyTimeline, contextEvents, handlePostAboutEvent }) => {
  const { t } = useTranslation();
  const et = 'dashboard.expandableStockCard.eventsTab';
  return (
  <>
    <div className="mb-4">
      <h3 className="text-lg font-bold text-gray-900 dark:text-slate-200 mb-1">{t(`${et}.recentEvents`)}</h3>
      <p className="text-xs text-gray-500 dark:text-slate-400">{t(`${et}.subtitle`)}</p>
    </div>

    {recencyTimeline.last24h.length > 0 && <TimelineSection title={t(`${et}.last24Hours`)} events={recencyTimeline.last24h} onPostAbout={handlePostAboutEvent} />}
    {recencyTimeline.last7d.length > 0 && <TimelineSection title={t(`${et}.last7Days`)} events={recencyTimeline.last7d} onPostAbout={handlePostAboutEvent} />}
    {recencyTimeline.last30d.length > 0 && <TimelineSection title={t(`${et}.last30Days`)} events={recencyTimeline.last30d} onPostAbout={handlePostAboutEvent} />}

    {contextEvents.length === 0 && (
      <div className="text-center py-8">
        <Calendar className="w-12 h-12 text-gray-400 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-slate-400 text-sm">{t(`${et}.noEventsTitle`)}</p>
        <p className="text-gray-500 dark:text-slate-500 text-xs mt-1">{t(`${et}.noEventsBody`)}</p>
      </div>
    )}
  </>
  );
};

const PostTab = ({
  displaySymbol,
  stock,
  composerContext,
  onCreatePost,
  setShowPostComposer,
  setComposerContext,
  contextEvents,
  handlePostAboutEvent,
  handlePostAboutChart,
  showPostComposer,
  // FIX: received from parent so the composer X button works
  onClose,
}) => {
  const { t } = useTranslation();
  const pt = 'dashboard.expandableStockCard.postTab';
  return (
  <>
    <div className="mb-4">
      <h3 className="text-lg font-bold text-gray-900 dark:text-slate-200 mb-1">{t(`${pt}.shareYourAnalysis`)}</h3>
      <p className="text-xs text-gray-500 dark:text-slate-400">
        {t(`${pt}.shareBody`)}
        {composerContext ? t(`${pt}.postingAboutType`, { type: composerContext.type }) : t(`${pt}.createAPost`)}
      </p>
    </div>

    <DashboardPostComposer
      activeStock={displaySymbol}
      watchlist={[stock]}
      stockData={stock}
      // contextData shape: { type: 'event'|'chart'|'summary', evt, prefill }
      // The composer's normaliseContextAttachments() bridges this internally.
      contextData={composerContext}
      // FIX: wire onClose so the X button in the composer header works
      onClose={onClose}
      onPostCreated={(post) => {
        onCreatePost?.(post);
        setShowPostComposer(false);
        setComposerContext(null);
      }}
      // Public posts are a disabled feature (featureFlags.posts=false, see
      // redux/indexRedux.js) -- without groupMode, this would still let users
      // submit to the public-posts backend endpoint (the flag only hides
      // frontend UI, it doesn't disable the route), a real loophole around
      // the feature being off everywhere else. Group posting is the enabled,
      // supported path, so this card composer should only ever post to a
      // group the user is actually a member of.
      groupMode
    />

    {!showPostComposer && contextEvents.length > 0 && (
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3">{t(`${pt}.orPostAboutEvent`)}</h4>
        <div className="space-y-2">
          {contextEvents.slice(0, 3).map((evt, i) => (
            <button key={i} onClick={() => handlePostAboutEvent(evt)} className="w-full text-left px-4 py-2 bg-cream-100 hover:bg-gray-100 dark:bg-slate-800/40 dark:hover:bg-slate-800/60 border border-cream-300 dark:border-slate-700/40 rounded-lg text-sm text-gray-600 dark:text-slate-300 transition-all">
              {evt.event_type === 'filing' ? t(`${pt}.filingEventLabel`, { form: evt.form }) : evt.event_type === 'news' ? (evt.headline?.substring(0, 80) || t(`${pt}.newsArticleFallback`)) : evt.event_type === 'sentiment' ? t(`${pt}.sentimentEventLabel`) : t(`${pt}.recentActivityLabel`)}
            </button>
          ))}
        </div>
      </div>
    )}

    {!showPostComposer && contextEvents.length === 0 && (
      <div className="text-center py-6">
        <button onClick={handlePostAboutChart} className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-sm text-blue-700 dark:text-blue-300 transition-all">
          <MessageSquare className="w-4 h-4 inline mr-2" />{t(`${pt}.shareFirstAnalysis`)}
        </button>
      </div>
    )}
  </>
  );
};

// === HELPER COMPONENTS ===

const StatCard = ({ label, value, icon, valueColor = 'text-gray-900 dark:text-slate-200' }) => (
  <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3">
    {icon && (
      <div className="flex items-center gap-2 mb-2">
        <div className="text-gray-500 dark:text-slate-400">{icon}</div>
        <div className="text-xs text-gray-500 dark:text-slate-400">{label}</div>
      </div>
    )}
    {!icon && <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">{label}</div>}
    <div className={`text-base sm:text-lg font-bold ${valueColor} break-words`}>{value}</div>
  </div>
);

const TimelineSection = ({ title, events, onPostAbout }) => (
  <div className="mb-4">
    <h4 className="text-sm font-medium text-gray-600 dark:text-slate-300 mb-3 flex items-center gap-2">
      <Clock className="w-4 h-4 text-gray-500 dark:text-slate-400" />{title}
    </h4>
    <div className="space-y-2">
      {events.map((evt, i) => <EventCard key={evt.id || i} evt={evt} onPostAbout={onPostAbout} />)}
    </div>
  </div>
);

const EventCard = ({ evt, onPostAbout }) => {
  const { t } = useTranslation();
  const ec = 'dashboard.expandableStockCard.eventCard';
  const getEventIcon = (type) => {
    switch(type) {
      case 'filing': return <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case 'news': return <Newspaper className="w-4 h-4 text-purple-600 dark:text-purple-400" />;
      case 'sentiment': return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'abnormal': return <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />;
      default: return <Activity className="w-4 h-4 text-gray-500 dark:text-slate-400" />;
    }
  };

  // ✅ FIX 1: Enhanced URL resolution with all possible field names
  const getEventUrl = (evt) => {
    // SEC filings - check multiple possible field names
    if (evt.event_type === 'filing') {
      return (
        evt.filing_url ||
        evt.filingUrl ||
        evt.url ||
        evt.source_url ||
        evt.sourceUrl ||
        evt.link ||
        // Construct SEC URL if we have the accession number
        (evt.accession_number ? `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${evt.ticker}&type=&dateb=&owner=exclude&filenum=${evt.accession_number}` : null)
      );
    }
    
    // News articles - check multiple possible field names
    if (evt.event_type === 'news') {
      return (
        evt.source_url ||
        evt.sourceUrl ||
        evt.article_url ||
        evt.articleUrl ||
        evt.url ||
        evt.link ||
        evt.news_url ||
        evt.newsUrl
      );
    }
    
    // Generic fallback for other event types
    return (
      evt.url ||
      evt.source_url ||
      evt.sourceUrl ||
      evt.link ||
      evt.stock_url ||
      evt.stockUrl
    );
  };

  const whyItMatters = getWhyItMatters(evt, t);
  const eventUrl = getEventUrl(evt);

  // Calculate metrics...
  const priceBefore = typeof evt.price_before === 'number' ? evt.price_before : null;
  const priceAfter = typeof evt.price_after === 'number' ? evt.price_after : null;
  const priceReactionPct = typeof evt.price_reaction_pct === 'number' ? evt.price_reaction_pct : 
    (priceBefore && priceAfter && priceBefore !== 0) ? ((priceAfter - priceBefore) / priceBefore) * 100 : null;
  const volumeRatio = typeof evt.volume_ratio === 'number' ? evt.volume_ratio : null;
  const sentimentBefore = typeof evt.sentiment_before === 'number' ? evt.sentiment_before : null;
  const sentimentAfter = typeof evt.sentiment_after === 'number' ? evt.sentiment_after : null;
  const sentimentDelta = (sentimentBefore !== null && sentimentAfter !== null) ? sentimentAfter - sentimentBefore : null;
  const abnormalityScore = typeof evt.abnormality_score === 'number' ? evt.abnormality_score : null;
  const confidence = typeof evt.confidence === 'number' ? evt.confidence : null;

  return (
    <div className="bg-cream-100 dark:bg-slate-800/40 border border-cream-300 dark:border-slate-700/40 rounded-lg p-3 hover:border-gray-300 dark:hover:border-slate-600/60 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{getEventIcon(evt.event_type)}</div>
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <span className="text-xs font-medium text-gray-600 dark:text-slate-300 flex items-center gap-1">
              {evt.event_type === 'filing' ? evt.form : t(`${ec}.marketActivity`)}
              {evt.event_type === 'filing' && evt.form && (
                <InfoTooltip content={getFilingTooltip(evt.form, t)} />
              )}
            </span>
            <span className="text-xs text-gray-500 dark:text-slate-500">{formatTimeAgo(evt, t)}</span>
          </div>

          {/* ✅ FIX 2: Make headline/title clickable if URL exists */}
          {eventUrl ? (
            <a 
              href={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-gray-900 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-300 mb-2 line-clamp-2 transition-colors cursor-pointer block"
            >
              {evt.headline || evt.title}
              <ExternalLink className="w-3 h-3 inline ml-1 opacity-50" />
            </a>
          ) : (
            <p className="text-sm text-gray-900 dark:text-slate-200 mb-2 line-clamp-2">
              {evt.headline || evt.title}
            </p>
          )}

          {evt.summary && <p className="text-xs text-gray-500 dark:text-slate-400 mb-2 line-clamp-2">{evt.summary}</p>}
          
          {/* Why it matters */}
          {(evt.driver_summary || evt.interpretation_hint || whyItMatters) && (
            <p className="text-xs text-gray-500 dark:text-slate-400 italic mb-2">
              <Info className="w-3 h-3 inline mr-1" />
              {t(`${ec}.whyThisMatters`, { reason: evt.driver_summary || evt.interpretation_hint || whyItMatters })}
            </p>
          )}

          {/* Price metrics */}
          {(priceBefore || priceAfter || priceReactionPct !== null || volumeRatio !== null) && (
            <div className="flex flex-wrap gap-3 mb-2 text-[11px] text-gray-500 dark:text-slate-400">
              {(priceBefore || priceAfter) && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-gray-500 dark:text-slate-500" />
                  {priceBefore && priceAfter ? t(`${ec}.priceRange`, { before: priceBefore.toFixed(2), after: priceAfter.toFixed(2) }) : priceAfter ? t(`${ec}.priceValue`, { value: priceAfter.toFixed(2) }) : t(`${ec}.priceValue`, { value: priceBefore.toFixed(2) })}
                </span>
              )}
              {priceReactionPct !== null && (
                <span className="flex items-center gap-1">
                  <Percent className="w-3 h-3 text-gray-500 dark:text-slate-500" />{formatPercentValue(priceReactionPct, 2, t)}
                </span>
              )}
              {volumeRatio !== null && (
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3 text-gray-500 dark:text-slate-500" />{t(`${ec}.volAvg`, { ratio: volumeRatio.toFixed(1) })}
                </span>
              )}
            </div>
          )}

          {/* Sentiment metrics */}
          {(sentimentBefore !== null || sentimentAfter !== null) && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] text-gray-500 dark:text-slate-500">{t(`${ec}.sentimentLabel`)}</span>
              <span className="text-[11px] text-gray-600 dark:text-slate-300">
                {t(`${ec}.sentimentRange`, { before: sentimentBefore !== null ? sentimentBefore.toFixed(2) : t('dashboard.expandableStockCard.na'), after: sentimentAfter !== null ? sentimentAfter.toFixed(2) : t('dashboard.expandableStockCard.na') })}
              </span>
              {sentimentDelta !== null && (
                <div className="flex items-center gap-1">
                  <div className="w-16 h-1.5 bg-gray-200 dark:bg-slate-700/50 rounded-full overflow-hidden">
                    <div className={`h-full ${sentimentDelta > 0 ? 'bg-emerald-500' : sentimentDelta < 0 ? 'bg-red-500' : 'bg-slate-400'}`} style={{ width: `${Math.min(100, Math.abs(sentimentDelta) * 100)}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-slate-400">{t(`${ec}.sentimentDelta`, { sign: sentimentDelta > 0 ? '+' : '', value: sentimentDelta.toFixed(2) })}</span>
                </div>
              )}
            </div>
          )}

          {/* Impact scores */}
          {(typeof evt.impact_score === 'number' || abnormalityScore !== null || confidence !== null) && (
            <div className="flex flex-wrap items-center gap-3 mb-2">
              {typeof evt.impact_score === 'number' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-slate-500">{t(`${ec}.impactLabel`)}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700/50 rounded-full overflow-hidden max-w-[120px]">
                    <div className={`h-full ${evt.impact_score > 7 ? 'bg-red-500' : evt.impact_score > 5 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${(evt.impact_score / 10) * 100}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-slate-400">{t(`${ec}.impactValue`, { value: evt.impact_score.toFixed(1) })}</span>
                </div>
              )}
              {abnormalityScore !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-slate-500">{t(`${ec}.abnormalityLabel`)}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 dark:bg-slate-700/50 rounded-full overflow-hidden max-w-[100px]">
                    <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (abnormalityScore / 10) * 100)}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 dark:text-slate-400">{t(`${ec}.impactValue`, { value: abnormalityScore.toFixed(1) })}</span>
                </div>
              )}
              {confidence !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-slate-500">{t(`${ec}.confidenceLabel`)}</span>
                  <span className="text-xs text-gray-600 dark:text-slate-300">{Math.round(confidence <= 1 ? confidence * 100 : confidence)}%</span>
                </div>
              )}
            </div>
          )}

          {/* ✅ FIX 3: Enhanced action buttons with better URL handling */}
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <button 
              onClick={(e) => { e.stopPropagation(); onPostAbout(evt); }} 
              className="text-xs px-2 py-1 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded transition-colors"
            >
              <MessageSquare className="w-3 h-3 inline mr-1" />
              {t(`${ec}.postAboutThis`)}
            </button>

            {/* ✅ Show link button if URL exists */}
            {eventUrl && (
              <a
                href={eventUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700/40 dark:hover:bg-slate-700/60 border border-gray-300 dark:border-slate-600/40 rounded text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" />
                {evt.event_type === 'filing' ? t(`${ec}.viewFiling`) : t(`${ec}.viewSource`)}
              </a>
            )}

            {/* ✅ Show source label if available */}
            {evt.source && (
              <span className="text-xs text-gray-500 dark:text-slate-500 flex items-center gap-1">
                <span className="opacity-50">•</span>
                {t(`${ec}.sourceLabel`, { source: evt.source })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpandableStockCard;